import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["periodType", "periodSelect", "periodSelectWrapper", "customDateWrapper", "startDate", "endDate"]
  static values = {
    url: String,
    frameId: String,
    fiscalYears: Array,
    academicYears: Array,
    trimesters: Array,
    pedagogicMonths: Array,
  }

  connect() {
    this.updatePeriodSelect()
  }

  onPeriodTypeChange(event) {
    const selected = event.currentTarget.dataset.value
    // Habillage GO4S DS : on bascule aria-selected (cf. .ds-segmented__btn)
    this.periodTypeTargets.forEach(btn => {
      btn.setAttribute("aria-selected", btn.dataset.value === selected ? "true" : "false")
    })
    this._selectedType = selected
    this.updatePeriodSelect()
    if (selected !== "custom") {
      this.submitFilter()
    }
  }

  onPeriodSelectChange() {
    this.submitFilter()
  }

  onCustomDateChange() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return
    const start = this.startDateTarget.value
    const end = this.endDateTarget.value
    if (start && end) {
      this.submitFilter()
    }
  }

  get selectedType() {
    return this._selectedType || this.element.dataset.defaultType || "month"
  }

  updatePeriodSelect() {
    const type = this.selectedType

    // Handle custom date wrapper visibility
    if (this.hasCustomDateWrapperTarget) {
      if (type === "custom") {
        this.customDateWrapperTarget.classList.remove("hidden")
      } else {
        this.customDateWrapperTarget.classList.add("hidden")
      }
    }

    if (!this.hasPeriodSelectTarget) return

    const select = this.periodSelectTarget
    const wrapper = this.hasPeriodSelectWrapperTarget ? this.periodSelectWrapperTarget : null

    // Types that need a secondary select
    const needsSelect = ["fiscal_year", "academic_year", "trimester", "pedagogic_month"]

    if (!needsSelect.includes(type)) {
      if (wrapper) wrapper.classList.add("hidden")
      return
    }

    if (wrapper) wrapper.classList.remove("hidden")

    let items = []
    switch (type) {
      case "fiscal_year":
        items = this.fiscalYearsValue || []
        break
      case "academic_year":
        items = this.academicYearsValue || []
        break
      case "trimester":
        items = this.trimestersValue || []
        break
      case "pedagogic_month":
        items = this.pedagogicMonthsValue || []
        break
    }

    // Preserve the user's current choice across rebuilds. Past years have neither
    // is_current nor is_active, so without this they would revert to the current
    // year every time the select is rebuilt. Fall back to is_active/is_current only
    // when there is no matching prior selection (e.g. after switching period type).
    const previous = select.value ? String(select.value) : null
    const hasPrevious = previous && items.some(item => String(item.id) === previous)
    const isSelected = (item) =>
      hasPrevious ? String(item.id) === previous : !!(item.is_active || item.is_current)

    const data = items.map(item => ({
      text: item.name,
      value: String(item.id),
      selected: isSelected(item),
    }))

    if (select.slim) {
      select.slim.setData(data)
    } else {
      select.innerHTML = ""
      items.forEach(item => {
        const option = document.createElement("option")
        option.value = item.id
        option.textContent = item.name
        if (isSelected(item)) option.selected = true
        select.appendChild(option)
      })
    }
  }

  submitFilter() {
    const type = this.selectedType
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("period_type", type)

    if (type === "custom") {
      if (this.hasStartDateTarget && this.startDateTarget.value) {
        url.searchParams.set("start_date", this.startDateTarget.value)
      }
      if (this.hasEndDateTarget && this.endDateTarget.value) {
        url.searchParams.set("end_date", this.endDateTarget.value)
      }
    } else {
      const needsId = ["fiscal_year", "academic_year", "trimester", "pedagogic_month"]
      if (needsId.includes(type) && this.hasPeriodSelectTarget && this.periodSelectTarget.value) {
        url.searchParams.set("period_id", this.periodSelectTarget.value)
      }
    }

    const frame = document.getElementById(this.frameIdValue)
    if (frame) {
      frame.src = url.toString()
    }
  }
}
